/**
 * CategorySection Component
 *
 * Displays a collapsible section of shopping items grouped by category.
 * Shows category name, item count, and individual shopping items with checkboxes.
 * Supports keyboard navigation and accessibility features.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CategoryGroup, ShoppingItem } from '../../types/shoppingList';
import './shopping.css';

interface CategorySectionProps {
  categoryGroup: CategoryGroup;
  onToggleItem: (itemId: number) => void;
}

export function CategorySection({ categoryGroup, onToggleItem }: CategorySectionProps) {
  const { t } = useTranslation('shopping');
  const [isCollapsed, setIsCollapsed] = useState(false);

  const handleToggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggleCollapse();
    }
  };

  return (
    <div className="category-section">
      <div
        className="category-header"
        onClick={handleToggleCollapse}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
        aria-expanded={!isCollapsed}
        aria-label={`${categoryGroup.category} category${isCollapsed ? ', collapsed' : ', expanded'}`}
        style={{ cursor: 'pointer' }}
      >
        <h2 className="category-name">
          <span style={{ marginRight: '0.5rem' }}>{isCollapsed ? '▶' : '▼'}</span>
          {t(`category_${categoryGroup.category}`, { defaultValue: categoryGroup.category })}
        </h2>
        <span className="category-count">
          {t('items', { count: categoryGroup.items.length })}
        </span>
      </div>

      {!isCollapsed && (
        <div className="category-items">
          {categoryGroup.items.map((item: ShoppingItem) => (
            <div
              key={item.id}
              className={`shopping-item ${item.checked ? 'checked' : ''}`}
            >
              <label className="shopping-item-label">
                <input
                  type="checkbox"
                  checked={item.checked}
                  onChange={() => onToggleItem(item.id)}
                  className="shopping-item-checkbox"
                  aria-label={`${item.ingredient_name}`}
                />
                <span className="shopping-item-content">
                  <span className="shopping-item-name">
                    {item.ingredient_name}
                  </span>
                  <span className="shopping-item-quantity">
                    {item.quantity} {item.unit}
                  </span>
                </span>
              </label>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default CategorySection;
